/* ==========================================================================
   THE BLUE FRAME ATELIER — global interactions.
   Vanilla JS, no framework. Reproduces the approved build's motion:
   scroll reveals (Framer whileInView) + the collection tile's cursor
   lamplight glow. Runs on load AND on Shopify Theme Editor section events
   so previews stay live while the merchant edits.
   ========================================================================== */
(function () {
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- scroll reveal (instant, zero delay) ---------------------------- */
  function initReveals(root) {
    var scope = root || document;
    var els = scope.querySelectorAll('.bf-reveal:not(.is-visible)');
    els.forEach(function (el) { el.classList.add('is-visible'); });
  }

  /* ---- collection tile cursor glow ------------------------------------ */
  function initTileGlow(root) {
    var scope = root || document;
    scope.querySelectorAll('.bf-tile').forEach(function (tile) {
      if (tile.dataset.bfGlow) return;
      tile.dataset.bfGlow = '1';
      var cx = 0, cy = 0, queued = false;
      tile.addEventListener('mousemove', function (e) {
        cx = e.clientX;
        cy = e.clientY;
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(function () {
          queued = false;
          var r = tile.getBoundingClientRect();
          tile.style.setProperty('--mx', ((cx - r.left) / r.width) * 100 + '%');
          tile.style.setProperty('--my', ((cy - r.top) / r.height) * 100 + '%');
        });
      }, { passive: true });
    });
  }

  /* ---- scroll parallax disabled to eliminate layout thrashing & scroll stickiness */
  function initParallax(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-bf-parallax]').forEach(function (el) {
      el.style.transform = 'none';
    });
  }

  /* ---- header: condense on scroll ------------------------------------- */
  function initHeaderScroll() {
    var header = document.querySelector('[data-bf-header]');
    if (!header || header.dataset.bfScrollBound) return;
    header.dataset.bfScrollBound = '1';
    var onScroll = function () {
      header.classList.toggle('bf-header--scrolled', window.scrollY > 60);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---- header: mobile drawer ------------------------------------------ */
  function initDrawer() {
    var drawer = document.querySelector('[data-bf-drawer]');
    if (!drawer || drawer.dataset.bfBound) return;
    drawer.dataset.bfBound = '1';
    var panel = drawer.querySelector('.bf-drawer__panel') || drawer;
    var triggers = document.querySelectorAll('[data-bf-drawer-open]');
    var lastTrigger = null;
    var FOCUSABLE = 'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])';
    var focusables = function () {
      return Array.prototype.slice.call(panel.querySelectorAll(FOCUSABLE))
        .filter(function (el) { return el.offsetParent !== null; });
    };
    var setExpanded = function (state) {
      triggers.forEach(function (b) { b.setAttribute('aria-expanded', state ? 'true' : 'false'); });
    };
    var open = function (e) {
      lastTrigger = (e && e.currentTarget) || document.activeElement;
      drawer.hidden = false;
      // next frame so the transition runs
      requestAnimationFrame(function () { drawer.classList.add('is-open'); });
      document.body.style.overflow = 'hidden';
      setExpanded(true);
      // move focus into the drawer (first focusable, usually Close)
      var f = focusables();
      if (f.length) f[0].focus();
    };
    var close = function () {
      drawer.classList.remove('is-open');
      document.body.style.overflow = '';
      setExpanded(false);
      setTimeout(function () { drawer.hidden = true; }, 450);
      // return focus to the control that opened the drawer
      if (lastTrigger && typeof lastTrigger.focus === 'function') lastTrigger.focus();
    };
    triggers.forEach(function (b) { b.addEventListener('click', open); });
    drawer.querySelectorAll('[data-bf-drawer-close]').forEach(function (b) { b.addEventListener('click', close); });
    document.addEventListener('keydown', function (e) {
      if (drawer.hidden) return;
      if (e.key === 'Escape') { close(); return; }
      // focus trap: keep Tab focus inside the open drawer
      if (e.key === 'Tab') {
        var f = focusables();
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });
  }

  /* ---- scroll-synced brand glyph -------------------------------------
     The header glyph cross-fades to a per-section icon. IntersectionObserver
     only (no scroll listener); the icon updates just once when a new section's
     middle band crosses the viewport middle — discrete, not continuous. */
  function initGlyph() {
    var glyph = document.querySelector('[data-bf-glyph]');
    if (!glyph || glyph.dataset.bfBound) return;
    var sections = document.querySelectorAll('[data-bf-icon]');
    if (!sections.length) return;
    glyph.dataset.bfBound = '1';

    var icons = {};
    glyph.querySelectorAll('svg[data-icon]').forEach(function (s) { icons[s.dataset.icon] = s; });
    var current = null;
    function setIcon(key) {
      if (!icons[key] || key === current) return;
      current = key;
      Object.keys(icons).forEach(function (k) { icons[k].classList.toggle('is-active', k === key); });
    }

    if (!('IntersectionObserver' in window)) return; // graceful: keeps default leaf
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) setIcon(e.target.getAttribute('data-bf-icon'));
      });
    }, { rootMargin: '-45% 0px -45% 0px', threshold: 0 });
    sections.forEach(function (s) { io.observe(s); });
  }

  /* ---- gifting: the unwrapping ribbon (plays once) -------------------- */
  function initGiftReveal(root) {
    var scope = root || document;
    var gift = scope.querySelector ? scope.querySelector('[data-bf-gift]') : null;
    if (!gift && scope !== document) gift = document.querySelector('[data-bf-gift]');
    if (!gift || gift.dataset.bfGiftBound) return;
    gift.dataset.bfGiftBound = '1';
    // reduced motion / no IO → leave the piece revealed (ribbon hidden)
    if (reduce || !('IntersectionObserver' in window)) return;
    gift.classList.add('bf-gift--armed'); // show the wrapped ribbon
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          gift.classList.add('bf-gift--open');
          io.disconnect(); // once only
        }
      });
    }, { threshold: 0.35 });
    io.observe(gift);
  }

  /* ---- long-form page: auto "On this page" table of contents -----------
     bf-page renders the Shopify page body ({{ page.content }}). Where the page
     opts in (data-toc="true") and has 2+ <h3> section headings, build a jump-link
     list from them and drop it in just before the first section. IDs are derived
     from the heading text so the anchors are stable and shareable. */
  function slugify(s) {
    return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'section';
  }
  function initPageToc(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-bf-page][data-toc="true"]').forEach(function (sec) {
      if (sec.__bfTocDone) return;
      sec.__bfTocDone = true;
      var content = sec.querySelector('[data-bf-page-content]');
      if (!content) return;
      var heads = content.querySelectorAll('h3');
      if (heads.length < 2) heads = content.querySelectorAll('h2');
      if (heads.length < 2) return;

      var used = {};
      var ol = document.createElement('ol');
      ol.className = 'bf-page__toc-list';
      heads.forEach(function (h) {
        var id = h.id;
        if (!id) {
          id = slugify(h.textContent);
          if (used[id]) { id = id + '-' + used[id]++; } else { used[id] = 1; }
          h.id = id;
        }
        var li = document.createElement('li');
        var a = document.createElement('a');
        a.href = '#' + id;
        a.textContent = h.textContent;
        li.appendChild(a);
        ol.appendChild(li);
      });

      var nav = document.createElement('nav');
      nav.className = 'bf-page__toc';
      nav.setAttribute('aria-label', 'On this page');
      var label = document.createElement('p');
      label.className = 'bf-tracked bf-page__toc-label';
      label.textContent = 'On this page';
      nav.appendChild(label);
      nav.appendChild(ol);

      content.insertBefore(nav, heads[0]);
    });
  }

  function initAll(root) {
    initReveals(root);
    initTileGlow(root);
    initParallax(root);
    initHeaderScroll();
    initDrawer();
    initGlyph();
    initGiftReveal(root);
    initPageToc(root);
  }

  if (document.readyState !== 'loading') initAll();
  else document.addEventListener('DOMContentLoaded', function () { initAll(); });

  /* ---- Theme Editor: re-init when a section is (re)loaded -------------- */
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:reorder', function () { initAll(); });
})();
